import type Phaser from "phaser";
import { audioManager } from "../../audio/AudioManager";
import { PLAYER, THRUST } from "../../config";
import { type ShipType, gameStore } from "../../state/GameStore";
import { loadSprite, validateTextures } from "../../utils/textureLoader";

// Ship texture keys (1024x1024 sprites for proper mipmapping)
export const SHIP_TEXTURES = {
  VOYAGER: {
    PRISTINE: "voyager-pristine",
    DAMAGED: "voyager-damaged",
    CRITICAL: "voyager-critical",
    FAILING: "voyager-failing",
  },
  DEFIANT: {
    PRISTINE: "defiant-pristine",
    DAMAGED: "defiant-damaged",
    CRITICAL: "defiant-critical",
    FAILING: "defiant-failing",
  },
  SHIELD: "shield",
} as const;

// Ship definitions
interface ShipConfig {
  scale: number;
  originX: number;
  originY: number;
  textures: {
    pristine: string;
    damaged: string;
    critical: string;
    failing: string;
  };
}

export const SHIPS: Record<ShipType, ShipConfig> = {
  VOYAGER: {
    scale: 0.0625, // 1024px sprite to ~64px game size
    originX: 0.5,
    originY: 0.5,
    textures: {
      pristine: SHIP_TEXTURES.VOYAGER.PRISTINE,
      damaged: SHIP_TEXTURES.VOYAGER.DAMAGED,
      critical: SHIP_TEXTURES.VOYAGER.CRITICAL,
      failing: SHIP_TEXTURES.VOYAGER.FAILING,
    },
  },
  DEFIANT: {
    scale: 0.0625, // 1024px sprite to ~64px game size
    originX: 0.49, // pivot x=501 in 1024px (from 95 at 194px)
    originY: 0.283, // pivot y=290 in 1024px (from 55 at 194px)
    textures: {
      pristine: SHIP_TEXTURES.DEFIANT.PRISTINE,
      damaged: SHIP_TEXTURES.DEFIANT.DAMAGED,
      critical: SHIP_TEXTURES.DEFIANT.CRITICAL,
      failing: SHIP_TEXTURES.DEFIANT.FAILING,
    },
  },
};

/** Get the currently selected ship config */
function getShip(): ShipConfig {
  return SHIPS[gameStore.selectedShip];
}

/**
 * Preload all player-related sprites.
 * Call this in the scene's preload() method.
 */
export function preloadPlayerSprites(loader: Phaser.Loader.LoaderPlugin): void {
  // Load all ship variants (both ships, for easy switching)
  for (const key of Object.values(SHIP_TEXTURES.VOYAGER)) {
    loadSprite(loader, key);
  }
  for (const key of Object.values(SHIP_TEXTURES.DEFIANT)) {
    loadSprite(loader, key);
  }
  loadSprite(loader, SHIP_TEXTURES.SHIELD);
}

// Sprite orientation offset: sprite points DOWN, Phaser rotation=0 is RIGHT
// Add π/2 (90°) to physics calculations so thrust/shooting match visual direction
const SPRITE_ANGLE_OFFSET = Math.PI / 2;

export class Player {
  private scene: Phaser.Scene;
  ship!: Phaser.Physics.Arcade.Sprite;
  private shield!: Phaser.GameObjects.Sprite;
  private thrustFlame!: Phaser.GameObjects.Sprite;

  private thrustCooldown = 0;
  private currentTexture = "";

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(): void {
    const ship = getShip();

    // Validate textures were loaded and have correct dimensions
    validateTextures(this.scene.textures, [
      ...Object.values(ship.textures),
      SHIP_TEXTURES.SHIELD,
    ]);

    this.createThrustTexture();

    // Initialize current texture
    this.currentTexture = ship.textures.pristine;

    // Create ship sprite using loaded texture
    this.ship = this.scene.physics.add.sprite(400, 300, ship.textures.pristine);
    this.ship.setScale(ship.scale);
    this.ship.setOrigin(ship.originX, ship.originY);
    this.ship.setDrag(PLAYER.DRAG);
    this.ship.setMaxVelocity(PLAYER.MAX_VELOCITY);
    this.ship.setCollideWorldBounds(false);

    // Create shield overlay (always visible for now)
    this.shield = this.scene.add.sprite(0, 0, SHIP_TEXTURES.SHIELD);
    this.shield.setScale(ship.scale * 1.3); // Slightly larger than ship
    this.shield.setAlpha(0.4);
    this.shield.setDepth(1); // Above ship

    // Create thrust flame
    this.thrustFlame = this.scene.add.sprite(0, 0, "thrust");
    this.thrustFlame.setVisible(false);
  }

  private createThrustTexture(): void {
    if (this.scene.textures.exists("thrust")) return;

    const graphics = this.scene.make.graphics();
    graphics.fillStyle(0xff4400);
    graphics.fillTriangle(0, 8, 8, 0, 8, 16);
    graphics.fillStyle(0xffaa00);
    graphics.fillTriangle(2, 8, 6, 2, 6, 14);
    graphics.generateTexture("thrust", 8, 16);
    graphics.destroy();
  }

  private getTextureForHealth(healthPercent: number): string {
    const ship = getShip();
    if (healthPercent > 75) return ship.textures.pristine;
    if (healthPercent > 50) return ship.textures.damaged;
    if (healthPercent > 25) return ship.textures.critical;
    return ship.textures.failing;
  }

  updateDamageVisual(): void {
    const newTexture = this.getTextureForHealth(gameStore.health);
    if (newTexture !== this.currentTexture) {
      this.currentTexture = newTexture;
      this.ship.setTexture(newTexture);
    }
  }

  update(
    _time: number,
    delta: number,
    controls: { rotateLeft: boolean; rotateRight: boolean; thrust: boolean },
  ): void {
    const shipBody = this.ship.body as Phaser.Physics.Arcade.Body;

    // Rotation
    if (controls.rotateLeft) {
      this.ship.setAngularVelocity(-PLAYER.ROTATION_SPEED);
    } else if (controls.rotateRight) {
      this.ship.setAngularVelocity(PLAYER.ROTATION_SPEED);
    } else {
      this.ship.setAngularVelocity(0);
    }

    // Thrust
    if (controls.thrust) {
      this.scene.physics.velocityFromRotation(
        this.ship.rotation + SPRITE_ANGLE_OFFSET,
        PLAYER.ACCELERATION,
        shipBody.acceleration,
      );
      this.thrustFlame.setVisible(true);

      // Thrust sound cooldown
      this.thrustCooldown -= delta;
      if (this.thrustCooldown <= 0) {
        audioManager.playThrust();
        this.thrustCooldown = THRUST.SOUND_COOLDOWN;
      }
    } else {
      this.ship.setAcceleration(0);
      this.thrustFlame.setVisible(false);
    }

    // Update thrust flame position (behind ship based on visual direction)
    const visualRotation = this.ship.rotation + SPRITE_ANGLE_OFFSET;
    this.thrustFlame.x =
      this.ship.x - Math.cos(visualRotation) * THRUST.FLAME_OFFSET;
    this.thrustFlame.y =
      this.ship.y - Math.sin(visualRotation) * THRUST.FLAME_OFFSET;
    this.thrustFlame.rotation = visualRotation + Math.PI;

    // Update shield overlay position (follows ship)
    this.shield.setPosition(this.ship.x, this.ship.y);
    this.shield.setRotation(this.ship.rotation);

    // Update damage visual based on health
    this.updateDamageVisual();
  }

  setInvulnerable(flashing: boolean, alpha?: number): void {
    if (flashing && alpha !== undefined) {
      this.ship.setAlpha(alpha);
    } else {
      this.ship.setAlpha(1);
    }
  }

  hide(): void {
    this.ship.setVisible(false);
    this.shield.setVisible(false);
    this.thrustFlame.setVisible(false);
  }

  getVelocity(): Phaser.Math.Vector2 {
    const body = this.ship.body as Phaser.Physics.Arcade.Body;
    return body.velocity;
  }
}
